import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

type Tier = "good" | "better" | "best";

interface PackagePickerProps {
  packagePrices?: { good?: number; better?: number; best?: number } | null;
  selected?: string | null;
  onSelect: (tier: Tier) => void;
  disabled?: boolean;
}

const TIER_META: Record<Tier, { label: string; sub: string }> = {
  good:   { label: "Good",   sub: "Builder-grade essentials" },
  better: { label: "Better", sub: "Architectural standard" },
  best:   { label: "Best",   sub: "Designer / lifetime upgrade" },
};

export const PackagePicker = ({ packagePrices, selected, onSelect, disabled }: PackagePickerProps) => {
  const prices = packagePrices || {};
  const hasAny = (prices.good || 0) + (prices.better || 0) + (prices.best || 0) > 0;
  if (!hasAny) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
      {(Object.keys(TIER_META) as Tier[]).map((tier) => {
        const isSelected = selected === tier;
        const price = Number(prices[tier] || 0);
        return (
          <Card
            key={tier}
            className={cn(
              "p-4 cursor-pointer transition-all border-2",
              isSelected ? "border-primary ring-2 ring-primary/20" : "border-border hover:border-primary/40",
            )}
            onClick={() => !disabled && onSelect(tier)}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-semibold uppercase tracking-wide">{TIER_META[tier].label}</span>
              {isSelected && <Check className="h-4 w-4 text-primary" />}
            </div>
            <div className="text-2xl font-bold">${price.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
            <div className="text-xs text-muted-foreground mt-1">{TIER_META[tier].sub}</div>
            {!isSelected && (
              <Button
                size="sm"
                variant="outline"
                className="mt-3 w-full"
                disabled={disabled}
                onClick={(e) => { e.stopPropagation(); onSelect(tier); }}
              >
                Select
              </Button>
            )}
          </Card>
        );
      })}
    </div>
  );
};
